import { parsedApiOrgUserActionsEnums } from '@/models/user-models';
import {
  GetSearchDocumentsResponseDocumentModel as ApiGetSearchDocumentsResponseDocumentModel,
  GetSearchDocumentsResponseGenericDocumentAssociationModel as ApiGetSearchDocumentsResponseGenericDocumentAssociationModel,
  GetSearchDocumentsResponseModel as ApiGetSearchDocumentsResponseModel,
  GetSearchDocumentsResponseSegmentInstanceDocumentAssociationModel as ApiGetSearchDocumentsResponseSegmentInstanceDocumentAssociationModel,
  GetSearchDocumentsResponseDocumentAconexMetadataDocumentModel as ApiGetSearchDocumentsResponseDocumentAconexMetadataDocumentModel,
  GetSearchDocumentsResponseDocumentAconexMetadataMailModel as ApiGetSearchDocumentsResponseDocumentAconexMetadataMailModel,
} from '@/services/generated/api';
import { OtUserAction } from '@/types/action-enums';
import { OtDocumentReceivalType, parsedOtDocumentReceivalType } from '@/types/document-receival-type';
import {
  OtSegmentInstanceDocumentVersionAssociationType,
  parsedOtSegmentInstanceDocumentVersionAssociationType,
} from '@/types/segment-instance-document-version-association-type';
export interface IDocumentAssociation {
  gid: string;
  createdDateUtc: Date;
  associatedGid: string;
  associatedCode: string;
  associatedName: string | null;
  availableActions: OtUserAction[];
}

export interface IDocumentsSegmentInstanceAssociation {
  gid: string;
  createdDateUtc: Date;
  associatedGid: string;
  associatedCode: string;
  associatedName: string | null;
  availableActions: OtUserAction[];
  // not sure we care about this for now
  // segmentProcessType: SegmentProcessType;
  segmentShortName: string;
  segmentName: string;
  associationType: OtSegmentInstanceDocumentVersionAssociationType;
  isLocked: boolean;
  documentVersionIndex: number;
  documentVersionReference: string;
  documentVersionName: string;
  documentVersionContentDescription: string | null;
  documentVersionDate: Date | null;
}

export interface IDocumentListItem {
  gid: string;
  documentVersionGid: string;
  reference: string;
  name: string;
  contentDescription: string | null;
  date: Date | null;
  receivalType: OtDocumentReceivalType | null;
  receivalDescription: string | null;
  deemedReceivedUtc: Date | null;
  deemedReceivedTimeZone: string | null;
  organisationAssociations: IDocumentAssociation[];
  projectAssociations: IDocumentAssociation[];
  contractAssociations: IDocumentAssociation[];
  segmentInstanceAssociations: IDocumentsSegmentInstanceAssociation[];
  availableActions: OtUserAction[];
  isSelected: boolean;
  versionIndex: number;
  isOutdatedVersion: boolean;
  isOrpaned: boolean;
  // I tire of the left hand right hand nonsense. Just going to use the API response models for this.
  aconexDocumentMetadata: ApiGetSearchDocumentsResponseDocumentAconexMetadataDocumentModel | null;
  aconexMailMetadata: ApiGetSearchDocumentsResponseDocumentAconexMetadataMailModel | null;
}

export interface IDocumentSearchResult {
  pageIndex: number;
  totalPages: number;
  totalCount: number;
  items: IDocumentListItem[];
}

export class DocumentAssociation implements IDocumentAssociation {
  public gid = '';
  public createdDateUtc!: Date;
  public associatedGid = '';
  public associatedCode = '';
  public associatedName: string | null = null;
  public availableActions: OtUserAction[] = [];
  constructor(data: IDocumentAssociation) {
    Object.assign(this, data);
  }
  static createFromApiResponse(
    data: ApiGetSearchDocumentsResponseGenericDocumentAssociationModel,
  ): DocumentAssociation {
    const result = new DocumentAssociation({
      gid: data.gid,
      associatedGid: data.associatedGid,
      associatedCode: data.associatedCode,
      associatedName: data.associatedName ?? null,
      createdDateUtc: data.createdDateUtc,
      availableActions: data.availableActions.map(action => parsedApiOrgUserActionsEnums[action]),
    });
    return result;
  }
}

export class DocumentsSegmentInstanceAssociation implements IDocumentsSegmentInstanceAssociation {
  public gid = '';
  public createdDateUtc!: Date;
  public associatedGid = '';
  public associatedCode = '';
  public associatedName: string | null = null;
  public availableActions: OtUserAction[] = [];
  // not sure we care about this for now
  // segmentProcessType: SegmentProcessType;
  public segmentShortName = '';
  public segmentName = '';
  public associationType!: OtSegmentInstanceDocumentVersionAssociationType;
  public isLocked = false;
  public documentVersionIndex!: number;
  public documentVersionReference = '';
  public documentVersionName = '';
  public documentVersionContentDescription: string | null = null;
  public documentVersionDate: Date | null = null;

  constructor(data: IDocumentsSegmentInstanceAssociation) {
    Object.assign(this, data);
  }
  static createFromApiResponse(
    data: ApiGetSearchDocumentsResponseSegmentInstanceDocumentAssociationModel,
  ): DocumentsSegmentInstanceAssociation {
    const result = new DocumentsSegmentInstanceAssociation({
      gid: data.gid,
      associatedGid: data.associatedGid,
      associatedCode: data.associatedCode,
      associatedName: data.associatedName ?? null,
      createdDateUtc: data.createdDateUtc,
      availableActions: data.availableActions.map(action => parsedApiOrgUserActionsEnums[action]),
      segmentShortName: data.segmentShortName,
      segmentName: data.segmentName,
      documentVersionIndex: data.documentVersionIndex,
      isLocked: data.isLocked,
      associationType: parsedOtSegmentInstanceDocumentVersionAssociationType[data.associationType],
      documentVersionReference: data.documentVersionReference,
      documentVersionName: data.documentVersionName,
      documentVersionContentDescription: data.documentVersionContentDescription ?? null,
      documentVersionDate: data.documentVersionDate ?? null,
    });
    return result;
  }
}

export class DocumentListItem implements IDocumentListItem {
  public gid = '';
  public documentVersionGid = '';
  public reference = '';
  public name = '';
  public contentDescription: string | null = null;
  public date: Date | null = null;
  public receivalType!: OtDocumentReceivalType | null;
  public receivalDescription!: string | null;
  public deemedReceivedUtc!: Date | null;
  public deemedReceivedTimeZone!: string | null;
  public organisationAssociations: DocumentAssociation[] = [];
  public projectAssociations: DocumentAssociation[] = [];
  public contractAssociations: DocumentAssociation[] = [];
  public segmentInstanceAssociations: DocumentsSegmentInstanceAssociation[] = [];
  public availableActions: OtUserAction[] = [];
  public isSelected = false;
  public versionIndex = 0;
  public isOutdatedVersion = false;
  public isOrpaned = false;
  public aconexDocumentMetadata: ApiGetSearchDocumentsResponseDocumentAconexMetadataDocumentModel | null = null;
  public aconexMailMetadata: ApiGetSearchDocumentsResponseDocumentAconexMetadataMailModel | null = null;

  constructor(data: IDocumentListItem) {
    Object.assign(this, data);
  }

  static createFromApiResponse(data: ApiGetSearchDocumentsResponseDocumentModel): DocumentListItem {
    const result = new DocumentListItem({
      gid: data.gid,
      documentVersionGid: data.documentVersionGid,
      reference: data.reference,
      name: data.name,
      contentDescription: data.contentDescription ?? null,
      date: data.date ?? null,
      organisationAssociations: data.organisationAssociations.map(a => DocumentAssociation.createFromApiResponse(a)),
      projectAssociations: data.projectAssociations.map(a => DocumentAssociation.createFromApiResponse(a)),
      contractAssociations: data.contractAssociations.map(a => DocumentAssociation.createFromApiResponse(a)),
      segmentInstanceAssociations: data.segmentInstanceAssociations.map(a =>
        DocumentsSegmentInstanceAssociation.createFromApiResponse(a),
      ),
      availableActions: data.availableActions.map(action => parsedApiOrgUserActionsEnums[action]),
      isSelected: false,
      receivalType: data.receivalType ? parsedOtDocumentReceivalType[data.receivalType] : null,
      receivalDescription: data.receivalDescription ?? null,
      deemedReceivedUtc: data.deemedReceivedUtc ?? null,
      deemedReceivedTimeZone: data.deemedReceivedTimeZone ?? null,
      aconexDocumentMetadata: data.aconexDocumentMetadata ?? null,
      aconexMailMetadata: data.aconexMailMetadata ?? null,
      versionIndex: data.versionIndex,
      isOutdatedVersion: false,
      isOrpaned:
        data.organisationAssociations.length === 0 &&
        data.projectAssociations.length === 0 &&
        data.contractAssociations.length === 0 &&
        data.segmentInstanceAssociations.length === 0,
    });
    return result;
  }
  public static createForOlderVersion(
    item: DocumentListItem,
    associations: DocumentsSegmentInstanceAssociation[],
  ): DocumentListItem {
    const result = new DocumentListItem({
      ...item,
      documentVersionGid: associations[0].gid,
      date: associations[0].documentVersionDate,
      reference: associations[0].documentVersionReference,
      name: associations[0].documentVersionName,
      contentDescription: associations[0].documentVersionContentDescription,
      isOutdatedVersion: true,
      isOrpaned: false, // We are not orphaned. We're being made specifically because we have associations for an outdated version.
      segmentInstanceAssociations: associations,
      organisationAssociations: [],
      projectAssociations: [],
      contractAssociations: [],
      // not sure about the available actions here. We can't do anything with an older version
      // we might be able to view it?
      availableActions: [],
    });
    return result;
  }
}
export class DocumentSearchResult implements IDocumentSearchResult {
  public pageIndex = 0;
  public totalPages = 0;
  public totalCount = 0;
  public items: DocumentListItem[] = [];

  constructor(data: IDocumentSearchResult) {
    Object.assign(this, data);
  }

  static createEmpty(): DocumentSearchResult {
    return new DocumentSearchResult({
      pageIndex: 1,
      totalPages: 1,
      totalCount: 0,
      items: [],
    });
  }

  static createFromApiResponse(data: ApiGetSearchDocumentsResponseModel): DocumentSearchResult {
    const result = new DocumentSearchResult({
      pageIndex: data.pageIndex ?? 1,
      totalPages: data.totalPages ?? 0,
      totalCount: data.totalCount ?? 0,
      items: data.items?.map(i => DocumentListItem.createFromApiResponse(i)) ?? [],
    });
    return result;
  }
}
